import React, { useCallback } from 'react';

import { Stack } from '@atlaskit/primitives/compiled';

import { useConfigPanelOptions } from './config-panel-options';
import type {
	ManagedBlockConfiguration,
	ManagedBlockConfigurationUpdate,
	ManagedBlockGoal,
	ManagedBlockOutputFormat,
} from './configuration';
import { GoalSelect } from './GoalSelect';
import { OutputFormatPicker } from './OutputFormatPicker';
import type { SmartCardProviderProduct } from './sources/smart-card-product';
import { SourcesPanel } from './sources/SourcesPanel';

type ConfigPanelProps = {
	configuration: ManagedBlockConfiguration;
	isDisabled?: boolean;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	smartCardProviderProduct?: SmartCardProviderProduct;
	sourceValidationError: 'invalid' | 'pending' | 'required' | null;
};

export const ConfigPanel = ({
	configuration,
	isDisabled = false,
	onChange,
	onConfigEdit,
	onInteraction,
	smartCardProviderProduct,
	sourceValidationError,
}: ConfigPanelProps): JSX.Element => {
	const { goalOptions, outputFormatOptions, sourceModeOptions, systemDesignOutputFormatOptions } =
		useConfigPanelOptions();

	const handleGoalChange = useCallback(
		(nextGoal: ManagedBlockGoal) => {
			const nextOutputFormat =
				nextGoal === 'system-design'
					? 'diagram'
					: configuration.outputFormat === 'diagram'
						? 'text'
						: configuration.outputFormat;

			onInteraction();

			if (configuration.goal !== nextGoal || configuration.outputFormat !== nextOutputFormat) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => {
				const currentNextOutputFormat =
					nextGoal === 'system-design'
						? 'diagram'
						: currentConfiguration.outputFormat === 'diagram'
							? 'text'
							: currentConfiguration.outputFormat;

				return {
					...currentConfiguration,
					goal: nextGoal,
					outputFormat: currentNextOutputFormat,
				};
			});
		},
		[configuration.goal, configuration.outputFormat, onChange, onConfigEdit, onInteraction],
	);

	const handleOutputFormatChange = useCallback(
		(outputFormat: ManagedBlockOutputFormat) => {
			onInteraction();

			if (configuration.outputFormat !== outputFormat) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				outputFormat,
			}));
		},
		[configuration.outputFormat, onChange, onConfigEdit, onInteraction],
	);

	return (
		<Stack space="space.150">
			<GoalSelect
				goal={configuration.goal}
				isDisabled={isDisabled}
				onGoalChange={handleGoalChange}
				options={goalOptions}
			/>
			<OutputFormatPicker
				goal={configuration.goal}
				isDisabled={isDisabled}
				onOutputFormatChange={handleOutputFormatChange}
				outputFormat={configuration.outputFormat}
				outputFormatOptions={outputFormatOptions}
				systemDesignOutputFormatOptions={systemDesignOutputFormatOptions}
			/>
			<SourcesPanel
				isDisabled={isDisabled}
				onChange={onChange}
				onConfigEdit={onConfigEdit}
				onInteraction={onInteraction}
				sourceEntries={configuration.sourceEntries}
				sourceMode={configuration.sourceMode}
				sourceModeOptions={sourceModeOptions}
				smartCardProviderProduct={smartCardProviderProduct}
				sourceValidationError={sourceValidationError}
				sourceUrls={configuration.sourceUrls}
			/>
		</Stack>
	);
};
